<template>
  <div class="relative">
    <div class="before-transparent"></div>
    <section class="bg-n3black text-white relative">
      <div class="container">
        <BackButton
          :url="
            $router.resolve({
              name: 'eventDetail',
              params: { slug: event.slug },
            })
          "
        />
        <div class="grid grid-cols-1 gap-4 sm:pt-0 pt-[6rem]">
          <div class="">
            <div
              class="relative overflow-hidden sm:mr-10 min-h-[300px]"
              :class="{
                'loading-content': fetchingShow || fetchingEvents,
              }"
            >
              <div class="text-center">
                <div class="text-title">How many tickets do you need?</div>

                <div class="w-full sm:w-2/6 inline-block my-10 text-[30px]">
                  <div
                    class="border border-n3yellow/60 rounded-lg shadow-lg mb-4 text-white p-4"
                    v-for="(category, index) in show.tickets"
                    :key="index"
                    :class="
                      category.active &&
                      (!currentCategory || index === currentCategory)
                        ? 'opacity-100'
                        : 'opacity-40'
                    "
                  >
                    <div class="flex items-center text-left">
                      <div class="flex-1">
                        <div class="text-lg font-semibold">
                          {{ index }}
                        </div>
                        <div class="opacity-50 text-base">
                          {{
                            moneyFormat(category.price, show.paymentCurrency)
                          }}
                        </div>
                        <div class="opacity-40 text-base">
                          {{ category.description }}
                        </div>
                      </div>
                      <div v-if="category.active" class="flex items-center">
                        <div>
                          <button
                            @click="decrease(index)"
                            class="text-n3yellow"
                          >
                            <span
                              class="material-icons inline-block align-middle mr-2"
                              >remove</span
                            >
                          </button>
                        </div>
                        <div class="font-semibold px-3">
                          {{ currentCategory === index ? ticketCount : 0 }}
                        </div>
                        <div>
                          <button
                            @click="increase(index)"
                            class="text-n3yellow"
                          >
                            <span
                              class="material-icons inline-block align-middle mr-2"
                              >add</span
                            >
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-5">
                  <default-button
                    :loading="requesting"
                    label="Continue"
                    classes="w-full sm:w-2/6"
                    :disable="ticketCount == 0 || requesting"
                    @clicked="handleContinue"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <ModalComponent
    :isOpen="guestModalState"
    @modal-close="(e) => (guestModalState = false)"
  >
    <template #content>
      <div class="p-5">
        <div class="text-2xl mb-6">Choose checkout method</div>

        <div
          :class="{ error: v$.email.$invalid && v$.email.$model.length > 1 }"
        >
          <label for="">Enter E-Mail address</label>
          <input
            type="text"
            ref="email"
            v-model="form.email"
            class="w-full px-0 pt-2 border-b-2 border-grey-400 n3-bg-grey outline-none opacity-50 focus:border-n3yellow mb-5"
          />
        </div>

        <a
          href=""
          @click.prevent="onContinueAsGuest"
          class="border-2 sm:px-[2rem] py-3 text-[20px] sm:text-[24px] border-n3grey inline-block disabled:opacity-50 rounded-lg w-full text-center opacity-70"
        >
          Continue as Guest
        </a>
        <div class="text-center my-6 opacity-50 text-xl">OR</div>

        <a
          href=""
          @click.prevent="redirectToLogin()"
          class="border-2 sm:px-[2rem] py-3 text-[20px] sm:text-[24px] border-n3yellow inline-block disabled:opacity-50 rounded-lg w-full text-center"
        >
          N3 Account
        </a>
      </div>
    </template>
  </ModalComponent>

  <component :is="'script'" type="application/ld+json"> {{ jsonLd }}</component>
</template>
<script>
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  reactive,
  toRefs,
  watch,
} from "@vue/runtime-core";

import { useStore } from "vuex";
import DefaultButton from "@/components/button/DefaultButton.vue";
import {
  moneyFormat,
  saveStorage,
  scrollToTop,
  seatNameShorter,
} from "@/utils/helper";
import { useRoute, useRouter } from "vue-router";
import BackButton from "@/components/button/BackButton.vue";
import { useMeta } from "vue-meta";
import NotEnoughTicketsLeftError from "@/utils/notEnoughTicketsLeftError";
import { useSweetAlert } from "@/composable/useSweetAlert";
import { useGoogleEventCard } from "@/composable/useGoogleEventCard";

import ModalComponent from "@/components/ModalComponent.vue";

import { required, email } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    DefaultButton,
    BackButton,
    ModalComponent,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { json, addEvent, resetEvents } = useGoogleEventCard();

    const { Toast } = useSweetAlert();

    const { proxy } = getCurrentInstance();

    const state = reactive({
      ticketCount: 0,
      requesting: false,
      guestModalState: false,
      form: {
        email: "",
      },
      currentCategory: null,
      event: computed(() => store.state.event.event),
      show: computed(() => store.state.show.show),
      reservation: computed(() => store.state.reservation.reservation),
      reservationID: computed(() => store.state.reservation.reservationID),
      fetchingEvents: computed(() => store.state.event.fetchingData),
      fetchingShow: computed(() => store.state.show.fetchingData),
      currentUser: computed(() => store.state.auth.currentUser),
      jsonLd: computed(() => {
        if (json.length) resetEvents();
        if (state.show.movie.title) {
          addEvent({
            title: state.show.movie.title,
            start: state.show.start.toDate(),
            end: state.show.start.toDate(),
            description: state.show.movie.description,
            image: state.show.movie.posterUrlLandscape,
            place: {
              name: state.show.cinema.name,
              street: state.show.cinema.street,
              city: state.show.cinema.city,
              postal: state.show.cinema.postal,
            },
          });
        }

        return json;
      }),
    });

    const v$ = useVuelidate({ email: { required, email } }, state.form);

    useMeta(
      computed(() => ({
        title: (state.event?.title ?? "Event") + " - Tickets",
        description: state.event?.description ?? "",
      }))
    );

    watch(
      () => state.reservation,
      (v) => {
        if (v) {
          state.ticketCount = v.tickets;
        }
      }
    );

    onUnmounted(() => {
      store.dispatch("event/setState", { key: "showHeader", value: false });
    });

    onMounted(() => {
      store.dispatch("event/setState", { key: "showHeader", value: true });
      scrollToTop();
      fetchSeats();
      if (state.reservation.id) {
        state.ticketCount = state.reservation.tickets;
      }
    });

    function fetchSeats() {
      store.dispatch("show/findShow", route.params.showSlug).then(() => {});
    }

    async function increase(category) {
      if (state.currentCategory !== category) {
        state.ticketCount = 0;
      }

      state.currentCategory = category;
      if (state.ticketCount < state.show.tickets[category].left) {
        if (state.ticketCount < state.show.maxTickets) {
          state.ticketCount++;
        } else {
          Toast.fire({
            icon: "warning",
            title: `Select up to ${state.show.maxTickets} tickets`,
          });
        }
      } else {
        Toast.fire({
          icon: "warning",
          title: `There are only ${
            state.show.tickets[state.currentCategory].left
          } tickets left`,
        });
      }
    }

    function decrease(category) {
      if (state.currentCategory === category && state.ticketCount > 0) {
        state.ticketCount--;
      }
    }

    function redirectToLogin() {
      saveStorage("REDIRECT_TO", location.pathname);
      router.push({ name: "login" });
    }

    function handleContinue() {
      if (state.show.guestCheckout && !state.currentUser) {
        state.guestModalState = true;
        return;
      }

      if (!state.currentUser) {
        redirectToLogin();
        return;
      }
      state.requesting = true;
      store
        .dispatch("show/reserveTickets", {
          tickets: state.ticketCount,
          category: state.currentCategory,
        })
        .then((data) => {
          store.dispatch("reservation/attachReservation", data.reservation);
          console.log("Transaction success...");

          router.push({
            name: "reservationSummary",
            params: { showSlug: state.show.slug },
          });
        })
        .catch((e) => {
          if (e instanceof NotEnoughTicketsLeftError) {
            Toast.fire({
              icon: "warning",
              title: e.message,
            });
          } else {
            console.log("Transaction failed...", e);
          }
        })
        .finally(() => (state.requesting = false));
    }

    function onContinueAsGuest() {
      if (proxy.v$.email.$invalid) {
        const { Toast } = useSweetAlert();
        Toast.fire({
          title: "Enter valid email address!",
          icon: "error",
        });
        return;
      }
      state.guestModalState = false;
      store.dispatch("auth/setAuth", {
        firstName: "Guest",
        name: "Guest",
        email: state.form.email,
      });
      store.dispatch("auth/setGuest", true);
      handleContinue();
    }

    return {
      ...toRefs(state),
      v$,
      handleContinue,
      increase,
      decrease,
      seatNameShorter,
      fetchSeats,
      moneyFormat,
      redirectToLogin,
      onContinueAsGuest,
    };
  },
};
</script>
